import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { X } from "@phosphor-icons/react";
import LabeledTextArea from "../../../Widgets/LabeledTextArea";
import LabeledDatePicker from "../../../Widgets/LabeledDatePicker";
import LabeledSelect, { SelectOption } from "../../../Widgets/LabeledSelect";

interface ConcernEntryFormValues {
  concernEntry: string;
  reportedDate: Date | null;
}

const getInitialForm = (): ConcernEntryFormValues => ({
  concernEntry: "",
  reportedDate: new Date(),
});

interface ConcernEntryModalProps {
  open: boolean;
  categoryLabel: string;
  onClose: () => void;
  onSave: (values: ConcernEntryFormValues, assignReportedByToSelf: boolean) => void;
  isSaving?: boolean;
  initialValues?: Partial<ConcernEntryFormValues>;
  /** Current user, to decide whether the "reported by" choice needs to be shown. */
  currentUserId?: number | null;
  currentUserName?: string;
  /** The entry's existing attribution, when editing. */
  entryReportedById?: number | null;
  entryReportedByName?: string | null;
}

export const ConcernEntryModal: React.FC<ConcernEntryModalProps> = ({
  open,
  categoryLabel,
  onClose,
  onSave,
  isSaving = false,
  initialValues,
  currentUserId = null,
  currentUserName = "",
  entryReportedById = null,
  entryReportedByName = null,
}) => {
  const { t } = useTranslation(["clientDetails"]);
  const [formValues, setFormValues] = useState<ConcernEntryFormValues>({
    ...getInitialForm(),
    ...initialValues,
  });
  const [assignReportedByToSelf, setAssignReportedByToSelf] = useState(false);

  useEffect(() => {
    if (open) {
      setFormValues({ ...getInitialForm(), ...initialValues });
      // Default: keep the original submitter as "reported by".
      setAssignReportedByToSelf(false);
    }
  }, [open, initialValues]);

  const handleChange =
    (field: keyof ConcernEntryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleDateChange = (value: Date | null) => {
    setFormValues((prev) => ({ ...prev, reportedDate: value }));
  };

  const isEditMode = Boolean(initialValues && Object.keys(initialValues).length > 0);
  const isSaveDisabled = !formValues.concernEntry.trim();
  // Editing someone else's entry is the only case where there's a real choice;
  // a new entry is always self-reported, and editing your own entry can't change.
  const canReassignReportedBy =
    isEditMode && entryReportedById != null && entryReportedById !== currentUserId;
  const reportedByOptions: SelectOption[] = isEditMode
    ? [
        {
          value: "original",
          label: entryReportedByName ?? t("clientDetails:areasOfConcern.originalSubmitter"),
        },
        ...(canReassignReportedBy
          ? [{ value: "self", label: currentUserName || t("clientDetails:areasOfConcern.myself") }]
          : []),
      ]
    : [{ value: "self", label: currentUserName || t("clientDetails:areasOfConcern.myself") }];
  const reportedByValue = isEditMode ? (assignReportedByToSelf ? "self" : "original") : "self";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h4">
          {isEditMode ? `Edit ${categoryLabel}` : `Add New ${categoryLabel}`}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }} aria-label="Close dialog">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container gap={7} paddingTop={7}>
          <LabeledTextArea
            label={categoryLabel}
            value={formValues.concernEntry}
            onChange={handleChange("concernEntry")}
            multiline
            minRows={3}
          />
          <LabeledDatePicker
            label="Date"
            value={formValues.reportedDate}
            onChange={handleDateChange}
            slotProps={{ popper: { disablePortal: false } }}
          />
          <LabeledSelect
            label={t("clientDetails:areasOfConcern.reportedByLabel")}
            value={reportedByValue}
            onChange={(e) => setAssignReportedByToSelf(e.target.value === "self")}
            options={reportedByOptions}
            disabled={!canReassignReportedBy}
          />
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={() => onSave(formValues, canReassignReportedBy && assignReportedByToSelf)}
          loading={isSaving}
          disabled={isSaveDisabled}
        >
          {isEditMode ? "Save Changes" : "Add"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ConcernEntryModal;
